import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Eye, EyeOff, Loader2, Send, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Shown when ID staff moves a task from "With contractor" (or any status) into
 * "Update customer". Loads every image from the task's contractor-updates timeline,
 * lets staff tick which ones the customer should be able to see, then PATCHes
 * per-image visibility flags and finally flips the task status.
 * If the task has no images, this component transparently moves the status without
 * opening any UI.
 */
export default function ReviewImagesBeforeCustomerDialog({
  open,
  onOpenChange,
  task,
  onDone,
}) {
  const [loading, setLoading] = useState(true);
  const [updates, setUpdates] = useState([]);
  // Local visibility overrides keyed by image_id → boolean (hidden_from_customer)
  const [hiddenMap, setHiddenMap] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || !task?.task_id) return;
    setLoading(true);
    api
      .get(`/tasks/${task.task_id}/contractor-updates`)
      .then(({ data }) => {
        if (cancelled) return;
        const arr = data.updates || [];
        setUpdates(arr);
        const init = {};
        arr.forEach((u) => (u.images || []).forEach((img) => {
          init[img.image_id] = !!img.hidden_from_customer;
        }));
        setHiddenMap(init);
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, task?.task_id]);

  const allImages = useMemo(() => {
    // Flatten all images across all updates. Newest updates first (they're sorted desc).
    const arr = [];
    updates.forEach((u) => {
      (u.images || []).forEach((img) => arr.push({ ...img, update_id: u.update_id, updated_by: u.created_by_name, when: u.created_at, note: u.note }));
    });
    return arr;
  }, [updates]);

  const hiddenCount = allImages.filter((img) => hiddenMap[img.image_id]).length;
  const visibleCount = allImages.length - hiddenCount;

  const toggle = (imageId) => {
    setHiddenMap((m) => ({ ...m, [imageId]: !m[imageId] }));
  };
  const setAll = (hidden) => {
    const m = {};
    allImages.forEach((img) => (m[img.image_id] = hidden));
    setHiddenMap(m);
  };

  const submit = async () => {
    setSaving(true);
    try {
      // Persist any visibility changes vs. server state.
      await Promise.all(
        allImages.map((img) => {
          const desired = !!hiddenMap[img.image_id];
          const current = !!img.hidden_from_customer;
          if (desired === current) return null;
          return api.patch(
            `/tasks/contractor-updates/${img.update_id}/images/${img.image_id}/visibility`,
            { hidden_from_customer: desired }
          );
        })
      );
      // Then flip the task status
      await api.patch(`/tasks/${task.task_id}`, { status: "waiting_on_customer" });
      toast.success(
        allImages.length === 0
          ? "Task moved to customer"
          : `Task moved to customer · ${visibleCount} image${visibleCount === 1 ? "" : "s"} visible, ${hiddenCount} hidden`
      );
      onDone && (await onDone());
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[900px] w-[95vw] rounded-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="review-images-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Review images before customer sees them</DialogTitle>
          <DialogDescription>
            Pick which of the contractor&rsquo;s photos should be shown to the homeowner when this task
            moves to <span className="font-semibold text-[#2C2A29]">Update customer</span>. Hidden images
            stay visible to the ID firm and contractor.
          </DialogDescription>
        </DialogHeader>

        {task && (
          <div className="card-soft !bg-[#F7F4EC] !py-3 text-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571] mb-0.5">Task</div>
            <div className="font-semibold text-[#2C2A29]">{task.title}</div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 py-3 -mx-1 px-1">
          {loading ? (
            <div className="text-center py-8 text-[#7A7571]">
              <Loader2 className="mx-auto animate-spin" size={20} />
            </div>
          ) : allImages.length === 0 ? (
            <div className="text-sm text-[#7A7571] italic p-6 text-center bg-[#FBF7EF] rounded-2xl">
              The contractor hasn&rsquo;t attached any photos yet — nothing to review. Click&nbsp;
              <span className="font-semibold text-[#2C2A29]">Update customer</span> to continue.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="text-[#7A7571]">
                  <span className="text-[#166534] font-semibold">{visibleCount} visible</span>
                  {" · "}
                  <span className="text-[#9F1239] font-semibold">{hiddenCount} hidden</span>
                  {" · "}
                  {allImages.length} total
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-ghost text-xs" onClick={() => setAll(false)}>
                    Show all
                  </button>
                  <button className="btn-ghost text-xs" onClick={() => setAll(true)}>
                    Hide all
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {allImages.map((img) => {
                  const hidden = !!hiddenMap[img.image_id];
                  const src = `${api.defaults.baseURL}/tasks/contractor-updates/${img.update_id}/images/${img.image_id}`;
                  return (
                    <label
                      key={img.image_id}
                      className={`relative block rounded-2xl overflow-hidden border-2 cursor-pointer transition-all ${
                        hidden ? "border-[#F87171]" : "border-transparent hover:border-[#2C2A29]/30"
                      }`}
                      data-testid={`review-image-${img.image_id}`}
                    >
                      <div className={`aspect-square bg-[#F7F4EC] ${hidden ? "opacity-50" : ""}`}>
                        <img src={src} alt="Update" loading="lazy" className="w-full h-full object-cover" />
                      </div>
                      <div className="absolute top-2 left-2">
                        <input
                          type="checkbox"
                          checked={!hidden}
                          onChange={() => toggle(img.image_id)}
                          className="w-5 h-5 accent-[#166534] cursor-pointer"
                          aria-label="Show to customer"
                        />
                      </div>
                      <div className="absolute top-2 right-2">
                        {hidden ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#9F1239] text-white text-[10px] font-semibold uppercase tracking-wider">
                            <EyeOff size={10} /> Hidden
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-semibold uppercase tracking-wider">
                            <Eye size={10} /> Visible
                          </span>
                        )}
                      </div>
                      <div className="px-2 py-1.5 bg-white text-[10px] text-[#7A7571] truncate" title={img.note || ""}>
                        {img.updated_by || "Unknown"} · {fmt(img.when)}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#EBE5D9]">
          <button className="btn-secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={submit}
            disabled={saving || loading}
            data-testid="review-images-confirm"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
            {saving ? "Saving…" : allImages.length === 0 ? "Update customer" : `Update customer · ${visibleCount} visible`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
